import { useEffect, useRef } from "react";
import type { VoiceModePhase } from "./useVoiceMode";
import { VoiceOrb } from "./VoiceOrb";
import "./VoiceOverlay.css";

interface VoiceOverlayProps {
  phase: VoiceModePhase;
  onExit: () => void;
  /** Spacebar handler — the mic button itself is rendered next to the
   *  chat input (in ChatBox) so it aligns with the input lane. */
  onToggleMic: () => void;
  getOutputVolume?: () => number;
  ducking?: boolean;
}

const TOGGLE_DEBOUNCE_MS = 150;

export function VoiceOverlay({
  phase,
  onExit,
  onToggleMic,
  getOutputVolume,
  ducking,
}: VoiceOverlayProps) {
  const lastToggleRef = useRef(0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onExit();
        return;
      }

      if (e.code !== "Space") return;
      if (e.repeat) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if (document.visibilityState !== "visible") return;

      const now = performance.now();
      if (now - lastToggleRef.current < TOGGLE_DEBOUNCE_MS) return;
      lastToggleRef.current = now;

      e.preventDefault();
      onToggleMic();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onExit, onToggleMic]);

  return (
    <div className="voice-embed" onClick={onExit}>
      <div className="voice-embed-orb" onClick={(e) => e.stopPropagation()}>
        <VoiceOrb phase={phase} size={336} getOutputVolume={getOutputVolume} ducking={ducking} />
      </div>
    </div>
  );
}
